import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormArray
} from '@angular/forms';
import { Router } from '@angular/router';
import { 
  Drug,
  MedicalTest, 
  CartQuantityItem,
  MedicalConsultation,
  Order, 
  OrderStatus, 
  User
} from '../../models';
import { 
  DataService,
  MedicalTestService,
  OrderService,
  UserProfileService 
} from '../../services';
import { first, mergeMap, tap } from 'rxjs/operators';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-edit-order',
  templateUrl: './edit-order.component.html',
  styleUrls: ['./edit-order.component.scss']
})
export class EditOrderComponent implements OnInit {
  editOrderForm: FormGroup;
  submitted = false;
  products: MedicalTest[] | Drug[] = [];
  order: Order;
  orderStatus: OrderStatus[];
  availableStatuses: OrderStatus[];
  addConsultation: boolean;
  productQuantities: number[] = [];
  consultationAmount: number;
  totalCartCost: number;
  orderToEdit: Order;
  isDrugsOrder: boolean = false;
  itemQuantities: { [id: number]: number } = {};
  itemAmounts: { [id: number]: string } = {};

  constructor(
    private formBuilder: FormBuilder,
    private dataService: DataService,
    private medicalTestService: MedicalTestService,
    private orderService: OrderService,
    private userProfileService: UserProfileService,
    private router: Router,
  ) { }

  ngOnInit(): void {
    this.orderStatus = this.dataService.getLocalOrderStatus();
    this.editOrderForm = this.formBuilder.group({
      name: [''],
      email: [''],
      telephone: [''],
      birthDate: [''],
      availability: [''],
      location: [''],
      note: [''],
      status: ['', Validators.required]
    });
    this.dataService.getLocalOrderToEdit().pipe(
      tap(order => {
        if (!order) {
          this.router.navigate(['admin']);
          return;
        }
        this.order = order as Order;
        this.addConsultation = !!this.order.medical_consultation;
        this.isDrugsOrder = !!this.order?.drugs?.length;
        this.products = this.isDrugsOrder ? this.order.drugs as Drug[] : this.order.medical_tests as MedicalTest[];
        this.productQuantities = this.order.quantity ?? [];
        this.setItemQuantitiesAndAmounts();
      }),
      mergeMap(() => this.userProfileService.getSingleUser(this.order?.user_id as number)),
      tap((user: User) => {
        if (user) {
          this.f.name.setValue(user.firstname + ' ' + user.lastname);
          this.f.email.setValue(user.email);
          this.f.telephone.setValue(this.order.telephone);
          this.f.birthDate.setValue(user.birth_date);
          this.f.availability.setValue(this.order.availability);
          this.f.location.setValue(this.order.location);
          this.f.note.setValue(this.order.note);
          this.f.status.setValue(this.order.status);
          this.setAvailableStatusOptions();
        }
        this.totalCartCost = this.getTotalCost();
      }),
      mergeMap(() => this.medicalTestService.getLocalMedicalConsultationAmount().pipe(first()))
    ).subscribe((response: MedicalConsultation) => {
      this.consultationAmount = Number(response?.amount) || 0;
    });
  }

  get f() {
    return this.editOrderForm.controls;
  }

  setAvailableStatusOptions(): void {
    if (this.order.medical_consultation &&
      (!this.order.medical_tests || this.order.medical_tests.length === 0)
      || (!this.order.drugs || this.order.drugs.length > 0)) {
      this.availableStatuses = [
        {
          status: 'Order Confirmation',
          description: 'Your order has been placed successfully.',
        },
        {
          status: 'Order Complete',
          description: 'This order has been completed.',
        }
      ];
      this.editOrderForm.patchValue({ status: 'Order Complete' });
    } else {
      const currentStatusIndex = this.orderStatus.findIndex(data =>
        data.status === this.order.status);
      if (currentStatusIndex !== -1) {
        this.availableStatuses = this.orderStatus.slice(currentStatusIndex, currentStatusIndex + 2);
      }
    }
  }

  getTotalCost(): number {
    return this.dataService.getCartTotalCost({
      items: this.products,
      itemQuantities: this.itemQuantities,
      itemAmounts: this.itemAmounts,
      isDrug: this.isDrugsOrder,
      addConsultation: this.addConsultation,
      consultationAmount: this.consultationAmount,
      selectedQuantityTypeIds: this.order.selected_quantity_type_ids,
      quantities: this.order.quantity
    });
  }

  setItemQuantitiesAndAmounts(): void {
    this.itemQuantities = {};
    this.itemAmounts = {};
    this.products.map((item, index) => {
      const quantity = this.order.quantity?.[index] || 1;
      const quantityTypeId = this.order.selected_quantity_type_ids?.[index];
      let price = '0';
      if (this.isDrugsOrder) {
        const drug = item as Drug;
        const selectedQuantityType = drug.quantity_types?.find(qt => qt.id === quantityTypeId);
        const price = selectedQuantityType?.selling_price || '0';
      } else {
        price = (item as MedicalTest).price || '0';
      }
      this.itemQuantities[item.id] = quantity;
      this.itemAmounts[item.id] = price;
    });
  }

  onSubmit() {
    const formData = new FormData();
    this.submitted = true;
    if (this.editOrderForm.invalid) {
      return;
    }
    const formValues = this.editOrderForm.value;
    formData.append('telephone', formValues.telephone);
    formData.append('note', formValues.note || '');
    formData.append('availability', formValues.availability);
    formData.append('location', formValues.location);
    formData.append('status', formValues.status);
    formData.append('id', String(this.order?.id));
    const today = new Date();
    const formattedDate = today.toISOString().split('T')[0];
    let executedAtArray: string[] = this.order?.executed_at ?
      [...this.order.executed_at] : [formattedDate];
    if (formValues.status !== this.order.status) {
      executedAtArray.push(formattedDate);
    } else {
      executedAtArray[executedAtArray.length - 1] = formattedDate;
    }
    formData.append('executedAt[]', executedAtArray.toString());
    if (this.isDrugsOrder) {
      formData.append('drugIds[]', this.products.map(item => item.id).toString());
    } else {
      formData.append('medicalTestIds[]', this.products.map(item => item.id).toString());
    }
    formData.append('quantity[]', JSON.stringify(this.orderToEdit?.quantity || []));
    this.orderService.updateOrder(this.order.id as number, formData).pipe(
      first()).subscribe(response => {
        Swal.fire({
          title: '',
          text: 'Order Edited',
          icon: 'success',
          confirmButtonColor: '#00ACB1',
        }).then(() => {
          this.router.navigate(['admin']);
        });
      },
        error => {
          Swal.fire({
            title: 'An error occurred',
            html: 'Please contact us at: <b>info@meshincentre.com</b>',
            icon: 'error',
            confirmButtonColor: '#f37b7b',
          });
        });
  }

}
